import { useState } from "react";
import type { Listing, SearchResult } from "@/lib/construction/types";
import { CompanyRow } from "@/components/construction/CompanyRow";
import { formatCount } from "@/lib/construction/normalise";

type CompanyListProps = {
  initial: Listing[];
  total: number;
  /** Filters for "Load more", passed to /api/construction/search. */
  filter: { regions?: string[]; towns?: string[]; trades?: string[] };
  label: string;
  showRegion?: boolean;
  pageSize?: number;
};

/**
 * Server-rendered company list (visible without JavaScript) with a progressive
 * "Show more" that pages through /api/construction/search.
 */
export function CompanyList({ initial, total, filter, label, showRegion = false, pageSize = 30 }: CompanyListProps) {
  const [items, setItems] = useState<Listing[]>(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMore = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ offset: String(items.length), limit: String(pageSize), sort: "size" });
      if (filter.regions?.length) params.set("regions", filter.regions.join(","));
      if (filter.towns?.length) params.set("towns", filter.towns.join(","));
      if (filter.trades?.length) params.set("trades", filter.trades.join(","));
      const response = await fetch(`/api/construction/search?${params.toString()}`);
      if (!response.ok) throw new Error(String(response.status));
      const data = (await response.json()) as SearchResult;
      setItems((current) => {
        const seen = new Set(current.map((l) => l.key));
        return [...current, ...data.items.filter((l) => !seen.has(l.key))];
      });
    } catch {
      setError("More companies could not be loaded just now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!items.length) {
    return <p className="rounded-2xl border border-dashed border-ink/15 bg-white p-6 text-ink-muted">No companies are listed here yet.</p>;
  }

  return (
    <div>
      <ul aria-label={label} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((l) => (
          <CompanyRow key={l.key} listing={l} showRegion={showRegion} />
        ))}
      </ul>
      <div className="mt-8 flex flex-col items-center gap-3 text-center">
        <p aria-live="polite" className="text-sm text-ink-muted">
          Showing {formatCount(items.length)} of {formatCount(total)}
        </p>
        {items.length < total ? (
          <button
            type="button"
            onClick={loadMore}
            disabled={loading}
            className="inline-flex h-11 items-center justify-center rounded-full border border-ink/20 px-6 text-sm font-semibold text-ink transition-colors hover:border-ink disabled:opacity-60"
          >
            {loading ? "Loading…" : "Show more companies"}
          </button>
        ) : null}
        {error ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
      </div>
    </div>
  );
}
